import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  Stack,
  alpha,
  useTheme,
  LinearProgress
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import SupervisorAccountRoundedIcon from "@mui/icons-material/SupervisorAccountRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { fNumber } from "../utils/formatNumber";

// ----------------------------------------------------------------------

const ROLE_DATA = {
  financial_1: {
    title: "Financial Officer I (Chief Financial Officer / Head Cashier)",
    shortName: "Financial I",
    badgeColor: "#059669",
    icon: AccountBalanceRoundedIcon,
    summary:
      "Oversees cooperative-wide treasury operations, whole-of-cooperative portfolios, multi-account deposit allocations, and statutory EMTL stamp duty accounting.",
    scope: "Cooperative-wide (Unrestricted Portfolio Access)",
    allowed: [
      "Access whole-of-cooperative portfolio totals and ledger reconciliations.",
      "Post direct deposits and withdrawals across all 10 financial accounts.",
      "Execute the Single Deposit Split Tool (Savings, Shares, Loan, Ileya, Takaful) with 1x Stamp Duty.",
      "Execute the Welfare & Emergency Dues Split Tool (Welfare, Minutes, Development) with 1x Stamp Duty.",
      "Deduct or waive EMTL Stamp Duty (₦50 on qualifying deposits ≥ ₦10,000) with justification.",
      "Underwrite cooperative loans, record application fees (₦1,000), and apply COT rates (0.1%).",
      "Search active cooperative members for loan guarantor assignment without portfolio restrictions.",
      "Delete erroneous ledger transactions with double-confirmation safety."
    ],
    restricted: [
      "Cannot modify member bio-data or KYC registry (reserved for Registrar).",
      "Cannot change system user roles or administrative security policies (reserved for Super Admin)."
    ],
    workflows: [
      {
        title: "How to Execute a 5-Way Single Deposit Split",
        steps: [
          "Navigate to Financials or Account Reconciliation and click 'Single Deposit Split Tool'.",
          "Search and select the depositing member.",
          "Enter the total deposit amount received (bank transfer or cash).",
          "Enter any Loan Repayment, Ileya Savings, or Takaful allocations.",
          "Review the EMTL Stamp Duty deduction (₦50 auto-calculated if deposit ≥ ₦10,000, or toggle waiver with reason).",
          "The system automatically assigns 70% of the remaining balance to Savings and 30% to Shares.",
          "Click 'Confirm & Post Split Deposit' to atomically update all ledgers and synchronize balances."
        ]
      },
      {
        title: "How to Post Welfare, Minutes & Development in One Step",
        steps: [
          "Open Welfare, Minutes, Development, or Reconciliation screens and click 'Post Dues Split (Dev/Min/Wel)'.",
          "Select the member and enter the total dues payment received.",
          "Review the single unified EMTL Stamp Duty (₦50 or waived).",
          "Click 'Split Remaining Equally (3-way)' for equal distribution, or manually enter the exact amount in each account box (Welfare, Minutes, Development) as needed.",
          "Confirm posting to credit all three dues ledgers in a single atomic database transaction."
        ]
      }
    ],
    faqs: [
      {
        q: "What happens when I waive EMTL Stamp Duty?",
        a: "When stamp duty is waived (with a mandatory reason like 'Cooperative policy exception'), Stamp Duty becomes ₦0.00. The full deposit is distributed to member accounts with zero deductions and no false unallocated deficit."
      },
      {
        q: "Can I choose guarantors who are assigned to Financial II or III?",
        a: "Yes. All Financial Officers (I, II, III) can select any active cooperative member as a guarantor, excluding the applicant themselves."
      }
    ]
  },

  financial_2: {
    title: "Financial Officer II (Branch / Portfolio Cashier)",
    shortName: "Financial II",
    badgeColor: "#0D9488",
    icon: PaymentsRoundedIcon,
    summary:
      "Handles frontline cashiering, savings collections, dues, and loan repayments for assigned member portfolios.",
    scope: "Assigned Member Portfolio Only",
    allowed: [
      "View dashboard totals and passbooks for members assigned to Financial II.",
      "Post cashier deposits and withdrawals for assigned portfolio members.",
      "Use the Single Deposit Split Tool (Savings, Shares, Loan, Ileya, Takaful).",
      "Use the Welfare Dues Split Tool (Dev, Min, Wel).",
      "Apply or waive EMTL Stamp Duty with mandatory audit notes.",
      "Search and assign loan guarantors from ALL active cooperative members across the system.",
      "Access Account Reconciliation and Stamp Duty analytics for portfolio members."
    ],
    restricted: [
      "Cannot view cooperative-wide executive totals (restricted to assigned portfolio).",
      "Cannot post transactions to members assigned to Financial I or III.",
      "Cannot edit member registration KYC documents or approval statuses."
    ],
    workflows: [
      {
        title: "Frontline Cashiering Workflow",
        steps: [
          "Log in and access your 'Assigned Portfolio' dashboard tab.",
          "Select the member making a deposit.",
          "Choose single-account deposit or click 'Single Deposit Split Tool' if distributing across multiple accounts.",
          "Verify the deposit amount against bank teller / receipt.",
          "Confirm transaction to update the member's passbook immediately."
        ]
      }
    ],
    faqs: [
      {
        q: "Why can't I see cooperative-wide totals?",
        a: "Financial II and III are structured for portfolio segregation. You have full access to your assigned member base while executive totals are reserved for Financial I and President."
      }
    ]
  },

  financial_3: {
    title: "Financial Officer III (Branch / Portfolio Cashier)",
    shortName: "Financial III",
    badgeColor: "#0284C7",
    icon: PaymentsRoundedIcon,
    summary:
      "Handles daily transaction entries, multi-account split postings, and dues management for Financial III portfolio members.",
    scope: "Assigned Member Portfolio Only",
    allowed: [
      "View dashboard totals and passbooks for members assigned to Financial III.",
      "Post cashier deposits and withdrawals for assigned portfolio members.",
      "Use the Single Deposit Split Tool (Savings, Shares, Loan, Ileya, Takaful).",
      "Use the Welfare Dues Split Tool (Dev, Min, Wel).",
      "Apply or waive EMTL Stamp Duty with audit justification.",
      "Search active cooperative members for loan guarantor assignment without portfolio boundaries.",
      "View reconciliation and transaction ledgers for assigned members."
    ],
    restricted: [
      "Cannot view cooperative-wide executive totals.",
      "Cannot post transactions for members assigned to Financial I or II.",
      "Cannot modify member KYC profiles or delete user accounts."
    ],
    workflows: [
      {
        title: "Processing Monthly Dues for Assigned Members",
        steps: [
          "Click 'Post Dues Split' from the Welfare or Minutes screen.",
          "Select your assigned member and enter total dues paid.",
          "Click 'Split Remaining Equally' to auto-distribute across Welfare, Minutes, and Development, or manually enter the exact amount in each account box.",
          "Submit to generate 3 balanced ledger credits simultaneously."
        ]
      }
    ],
    faqs: [
      {
        q: "Can a member assigned to me guarantee a loan for a member assigned to Financial I?",
        a: "Yes. Guarantor eligibility is cooperative-wide. As long as the guarantor is an Active member and distinct from the borrower, they qualify."
      }
    ]
  },

  president: {
    title: "President (Executive Governance & Audit)",
    shortName: "President",
    badgeColor: "#0B3B60",
    icon: SupervisorAccountRoundedIcon,
    summary:
      "Provides executive oversight, financial governance, strategic auditing, and whole-of-cooperative health monitoring.",
    scope: "Cooperative-wide (Executive View-Only)",
    allowed: [
      "Access the President Executive Dashboard with whole-of-cooperative asset totals.",
      "View all transaction ledgers across all 10 product lines (Savings, Shares, Loans, Business, SpeAcc, Dev, Min, Wel, Ileya, Takaful).",
      "Monitor cooperative loan portfolio health, delinquency rates, and repayment progress.",
      "Review Business commodity financing portfolio and profit earnings.",
      "Inspect EMTL Stamp Duty collection metrics and capital preserved via waivers.",
      "Review system Activity Logs and administrative audit trails.",
      "View member profiles, referee details, and guarantor records in read-only mode."
    ],
    restricted: [
      "Strictly RESTRICTED from posting cashier deposits, withdrawals, or split distributions.",
      "Single Deposit Split Tool and Welfare Dues Split Tool are hidden from view.",
      "Cannot edit or delete member profiles, bio-data, or ledger records.",
      "Cannot modify interest rates, COT rates, or profit margins."
    ],
    workflows: [
      {
        title: "Executive Audit & Health Review",
        steps: [
          "Navigate to 'President' menu on the left navigation bar.",
          "Review aggregate savings, active loan exposures, and business financing portfolios.",
          "Click on any individual member to inspect detailed passbooks and payment consistency.",
          "Access 'Activity Logs' to review administrative operations and security events."
        ]
      }
    ],
    faqs: [
      {
        q: "Why are transaction creation buttons hidden on President accounts?",
        a: "Separation of duties: The President exercises executive governance and supervisory audit, while cashiering operations are restricted to Financial Officers to ensure banking-standard dual control."
      }
    ]
  },

  business: {
    title: "Business Officer (Trade & Commodity Financing)",
    shortName: "Business Officer",
    badgeColor: "#D97706",
    icon: BusinessCenterRoundedIcon,
    summary:
      "Manages commodity purchase financing (Murabaha asset acquisition), vendor procurement costs, profit margin calculations, and installment tracking.",
    scope: "Business Financing Portfolio",
    allowed: [
      "Access Business Overview dashboard with portfolio health, total financing, and profit margin yields.",
      "Record 'Buy for User' asset purchases paid to suppliers on behalf of members.",
      "Use the interactive Business Profit Calculator to project markups and monthly installments.",
      "Select standardized repayment tenors (3m @ 5%, 6m @ 10%, 9m @ 12%) or custom margins.",
      "Record member repayment installments with EMTL Stamp Duty controls.",
      "Restructure tenors when approved, recording audit history in tenorHistory."
    ],
    restricted: [
      "Single Deposit Split Tool is hidden from Business accounts.",
      "Cannot access general cooperative savings/shares cashiering tools.",
      "Cannot modify member KYC profiles or assign system roles."
    ],
    workflows: [
      {
        title: "Financing an Asset Purchase for a Member",
        steps: [
          "Navigate to Business menu and click 'Add Business Transaction'.",
          "Select 'Buy for User (+)' as the transaction type.",
          "Enter the Item Purchase Cost (actual amount paid to supplier).",
          "Select repayment tenor: 3 Months (5%), 6 Months (10%), or 9 Months (12%).",
          "The system calculates the exact Profit Margin Amount, Total Financed Amount, and Monthly Installment.",
          "Save the transaction to disburse financing into the member's business liability account."
        ]
      }
    ],
    faqs: [
      {
        q: "How does the Business Profit Calculator calculate monthly installments?",
        a: "Formula: Installment = (Cost + (Cost × Margin% / 100)) / Tenor Months. For example, ₦100,000 for 6 months @ 10% = ₦110,000 total / 6 = ₦18,333.33 per month."
      }
    ]
  },

  admin: {
    title: "Administrator / Super Admin (System & Platform Control)",
    shortName: "Administrator",
    badgeColor: "#7C3AED",
    icon: AdminPanelSettingsRoundedIcon,
    summary:
      "Full administrative authority over platform configuration, user roles, security policies, ledger integrity, and audit logging.",
    scope: "Complete System Control",
    allowed: [
      "Full, unrestricted access across all cooperative modules and ledgers.",
      "Provision user accounts, allocate members to financial officers, and assign roles.",
      "Configure interest rates, COT rates, profit margins, and stamp duty defaults.",
      "Execute Single Deposit Split Tool and Welfare Dues Split Tool.",
      "Manage Account Reconciliation, Bank Reconciliation, and ledger dispute resolutions.",
      "Review complete system Activity Logs and security events.",
      "Edit member KYC records and perform authorized record deletions with confirmation prompts."
    ],
    restricted: [
      "All actions are permanently recorded in Activity Logs for transparency and audit accountability."
    ],
    workflows: [
      {
        title: "Allocating Members to Financial Officers",
        steps: [
          "Navigate to 'User' menu on the left navigation bar.",
          "Click on the member profile to edit.",
          "Under 'Allocated Financial Officer', select Financial I, II, or III.",
          "Save changes to update portfolio boundaries immediately."
        ]
      }
    ],
    faqs: [
      {
        q: "Can the Administrator reverse an incorrect transaction?",
        a: "Yes. The Administrator can use the modern TransactionTable Delete action (protected by double confirmation) or post a compensating adjustment entry."
      }
    ]
  },

  registrar: {
    title: "Registrar / Secretary (Membership & KYC Registry)",
    shortName: "Registrar",
    badgeColor: "#475569",
    icon: HowToRegRoundedIcon,
    summary:
      "Oversees member onboarding, bio-data records, KYC documentation, next-of-kin entries, referee validation, and membership approval statuses.",
    scope: "Member Registry & KYC Documentation",
    allowed: [
      "Register new cooperative members and issue Membership IDs.",
      "Update member bio-data: address, phone, place of work, home town, and next-of-kin.",
      "Record referee information and signatures.",
      "Manage membership approval statuses ('Active', 'Pending', 'Suspended').",
      "Inspect member profiles and verify identity credentials."
    ],
    restricted: [
      "Strictly RESTRICTED from posting financial deposits, withdrawals, or loan disbursements.",
      "Single Deposit Split Tool and Welfare Split Tool are hidden.",
      "Cannot modify member financial balances or delete transaction history."
    ],
    workflows: [
      {
        title: "Onboarding and Approving a New Member",
        steps: [
          "Click 'Add Member' from the User management screen.",
          "Fill in personal details, contact address, occupation, and next-of-kin.",
          "Add referee information and referee contact details.",
          "Set approval status to 'Active' upon verification.",
          "Save the profile to enable financial services for the member."
        ]
      }
    ],
    faqs: [
      {
        q: "Can the Registrar edit account balances?",
        a: "No. Financial data is strictly protected. The Registrar oversees documentation and identity records, while all balance changes occur through financial cashier ledgers."
      }
    ]
  },

  member: {
    title: "General Member (Customer / Self-Service Portal)",
    shortName: "General Member",
    badgeColor: "#1E293B",
    icon: PersonRoundedIcon,
    summary:
      "Self-service member portal to view personal financial assets, passbooks, loan repayment statuses, and download certified reports.",
    scope: "Personal Financial Records Only",
    allowed: [
      "View personal balances across all 10 accounts (Savings, Shares, Loan, Business, SpeAcc, Dev, Min, Wel, Ileya, Takaful).",
      "Inspect detailed transaction history using modern, searchable tables.",
      "Check active loan liabilities, monthly repayment schedules, and tenor progress.",
      "Track Business asset financing balances and monthly installments.",
      "Generate and download certified digital account statements and certificates."
    ],
    restricted: [
      "Cannot view other members' accounts or cooperative administrative totals.",
      "Cannot post transactions or modify profile details (changes must be requested through the Registrar)."
    ],
    workflows: [
      {
        title: "Checking Account Statements & Passbooks",
        steps: [
          "Log in to your member dashboard.",
          "Click on any product card (e.g. 'Savings' or 'Shares') on the left menu.",
          "Use the modern search bar to filter transactions by date, amount, or description.",
          "Check your latest verified balance displayed at the top of the passbook."
        ]
      }
    ],
    faqs: [
      {
        q: "What should I do if a deposit hasn't appeared in my passbook?",
        a: "Contact your assigned Financial Officer with your bank transfer receipt or teller number. They can look up your record in Account Reconciliation."
      }
    ]
  }
};

// ----------------------------------------------------------------------
// Interactive Calculator Demonstrators
// ----------------------------------------------------------------------

const parseNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

function InteractiveCalculators({ userRole = "user", userIsAdmin = false, selectedGuideRole = "financial_1" }) {
  const theme = useTheme();

  // Tier detection:
  // 1. Executive & Financial Suite: Admin, President, Financial I, II, III
  //    Has access to all calculators (1-9)
  const isExecutiveOrFinancial = useMemo(() => {
    return (
      userIsAdmin ||
      ["admin", "president", "financial_1", "financial_2", "financial_3"].includes(userRole)
    );
  }, [userIsAdmin, userRole]);

  // 2. Business Officer Suite: Business role
  //    Has access to 1-4 + Business-relevant formulas (5 & 6)
  const isBusinessOfficer = useMemo(() => {
    return userRole === "business";
  }, [userRole]);

  // Preview switch (for Executive / Financial users to preview views)
  const [previewTier, setPreviewTier] = useState("auto"); // "auto", "executive", "business", "member"

  const effectiveTier = useMemo(() => {
    if (previewTier !== "auto") return previewTier;
    if (isExecutiveOrFinancial) return "executive";
    if (isBusinessOfficer) return "business";
    return "member";
  }, [previewTier, isExecutiveOrFinancial, isBusinessOfficer]);

  const canSeeBusinessExtras = effectiveTier === "executive" || effectiveTier === "business";
  const canSeeFinancialExtras = effectiveTier === "executive";

  // 1. EMTL Calculator
  const [emtlDeposit, setEmtlDeposit] = useState("25,000");
  const [emtlWaived, setEmtlWaived] = useState(false);

  const cleanEmtlDeposit = parseNum(emtlDeposit);
  const isQualifying = cleanEmtlDeposit >= 10000;
  const emtlDuty = emtlWaived ? 0 : isQualifying ? 50 : 0;
  const emtlNet = Math.max(0, cleanEmtlDeposit - emtlDuty);

  // 2. Single Deposit Split Tool
  const [splitDeposit, setSplitDeposit] = useState("50,000");
  const [splitLoan, setSplitLoan] = useState("10,000");
  const [splitIleya, setSplitIleya] = useState("5,000");
  const [splitTakaful, setSplitTakaful] = useState("2,500");
  const [splitDutyWaived, setSplitDutyWaived] = useState(false);

  const cleanSplitDeposit = parseNum(splitDeposit);
  const cleanLoan = parseNum(splitLoan);
  const cleanIleya = parseNum(splitIleya);
  const cleanTakaful = parseNum(splitTakaful);

  const splitDuty = splitDutyWaived ? 0 : cleanSplitDeposit >= 10000 ? 50 : 0;
  const splitFixed = cleanLoan + cleanIleya + cleanTakaful + splitDuty;
  const splitDeficit = splitFixed > cleanSplitDeposit ? splitFixed - cleanSplitDeposit : 0;
  const splitResidual = Math.max(0, cleanSplitDeposit - splitFixed);
  const splitSavings = Number((splitResidual * 0.7).toFixed(2));
  const splitShares = Number((splitResidual - splitSavings).toFixed(2));

  // 3. Welfare Dues 3-Way Split
  const [duesDeposit, setDuesDeposit] = useState("15,000");
  const [duesWaived, setDuesWaived] = useState(false);

  const cleanDues = parseNum(duesDeposit);
  const duesDuty = duesWaived ? 0 : cleanDues >= 10000 ? 50 : 0;
  const duesNet = Math.max(0, cleanDues - duesDuty);
  const duesBase = Number((duesNet / 3).toFixed(2));
  const duesMinutes = duesBase;
  const duesDev = duesBase;
  const duesWelfare = Number((duesNet - duesMinutes - duesDev).toFixed(2));

  // 4. Business Profit Calculator (Standard 3/6/9M Markup)
  const [bizCost, setBizCost] = useState("150,000");
  const [bizTenor, setBizTenor] = useState(6);
  const [bizRate, setBizRate] = useState(10);

  const cleanBizCost = parseNum(bizCost);
  const bizProfit = Number(((cleanBizCost * bizRate) / 100).toFixed(2));
  const bizTotal = Number((cleanBizCost + bizProfit).toFixed(2));
  const bizInstallment = bizTenor > 0 ? Number((bizTotal / bizTenor).toFixed(2)) : 0;

  // 5. Business Tenor Restructuring & Margin Modification Delta (Section 2.4.5)
  const [restructCost, setRestructCost] = useState("200,000");
  const [origTenor, setOrigTenor] = useState(6);
  const [origMargin, setOrigMargin] = useState(10);
  const [newTenor, setNewTenor] = useState(9);
  const [newMargin, setNewMargin] = useState(12);

  const cleanRestructCost = parseNum(restructCost);
  const origProfit = Number(((cleanRestructCost * origMargin) / 100).toFixed(2));
  const origTotal = Number((cleanRestructCost + origProfit).toFixed(2));
  const origInstallment = origTenor > 0 ? Number((origTotal / origTenor).toFixed(2)) : 0;

  const newProfit = Number(((cleanRestructCost * newMargin) / 100).toFixed(2));
  const newTotal = Number((cleanRestructCost + newProfit).toFixed(2));
  const newInstallment = newTenor > 0 ? Number((newTotal / newTenor).toFixed(2)) : 0;

  const deltaDebt = Number((newTotal - origTotal).toFixed(2));
  const deltaInstallment = Number((newInstallment - origInstallment).toFixed(2));

  // 6. Business Commodity Repayment & Portfolio Exposure Simulator (Section 2.6)
  const [expTotalFinanced, setExpTotalFinanced] = useState("330,000");
  const [expRepaid, setExpRepaid] = useState("146,666.68");
  const [expMonthlyDue, setExpMonthlyDue] = useState("36,666.67");

  const cleanExpTotal = parseNum(expTotalFinanced);
  const cleanExpRepaid = parseNum(expRepaid);
  const cleanExpMonthly = parseNum(expMonthlyDue);

  const expBalance = Math.max(0, Number((cleanExpTotal - cleanExpRepaid).toFixed(2)));
  const expProgress = cleanExpTotal > 0 ? Math.min(100, Math.round((cleanExpRepaid / cleanExpTotal) * 100)) : 0;
  const expMonthsLeft = cleanExpMonthly > 0 ? Math.ceil(expBalance / cleanExpMonthly) : 0;

  // 7. Cooperative Loan Facility, COT & Amortization Simulator (Section 2.5)
  const [loanPrincipal, setLoanPrincipal] = useState("500,000");
  const [loanCotRate, setLoanCotRate] = useState(0.1);
  const [loanTenor, setLoanTenor] = useState(6);
  const loanFormFee = 1000; // Flat ₦1,000 fee per Section 2.5

  const cleanLoanPrincipal = parseNum(loanPrincipal);
  const loanCotAmount = Number(((cleanLoanPrincipal * loanCotRate) / 100).toFixed(2));
  const loanTotalDebt = Number((cleanLoanPrincipal + loanCotAmount).toFixed(2));
  const loanMonthlyAmort = loanTenor > 0 ? Number((loanTotalDebt / loanTenor).toFixed(2)) : 0;

  // 8. EMTL Stamp Duty Portfolio Aggregations & Member Capital Preserved (Section 2.1)
  const [aggQualifyingCount, setAggQualifyingCount] = useState("150");
  const [aggAvgAmount, setAggAvgAmount] = useState("35,000");
  const [aggWaivedCount, setAggWaivedCount] = useState("32");

  const cleanAggCount = parseNum(aggQualifyingCount);
  const cleanAggAvg = parseNum(aggAvgAmount);
  const cleanAggWaived = parseNum(aggWaivedCount);

  const aggVolume = cleanAggCount * cleanAggAvg;
  const aggDeductedCount = Math.max(0, cleanAggCount - cleanAggWaived);
  const aggTotalDeducted = aggDeductedCount * 50;
  const aggCapitalPreserved = cleanAggWaived * 50;
  const aggNetMemberCredit = aggVolume - aggTotalDeducted;

  // 9. 10-Product Running Balance Continuity & Ledger Integrity Simulator (Section 2.6)
  const [balAccountType, setBalAccountType] = useState("asset"); // "asset" or "liability"
  const [balStarting, setBalStarting] = useState("250,000");
  const [balCredit, setBalCredit] = useState("60,000");
  const [balDebit, setBalDebit] = useState("25,000");

  const cleanBalStarting = parseNum(balStarting);
  const cleanBalCredit = parseNum(balCredit);
  const cleanBalDebit = parseNum(balDebit);

  const balEnding = balAccountType === "asset"
    ? Number((cleanBalStarting + cleanBalCredit - cleanBalDebit).toFixed(2))
    : Math.max(0, Number((cleanBalStarting + cleanBalCredit - cleanBalDebit).toFixed(2)));
  const balNetDelta = Number((cleanBalCredit - cleanBalDebit).toFixed(2));

  return (
    <Stack spacing={4}>
      {/* Tier & Access Level Indicator Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: effectiveTier === "executive" ? "#F0FDF4" : effectiveTier === "business" ? "#FFFBEB" : "#F8FAFC",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityRoundedIcon sx={{ fontSize: 20, color: effectiveTier === "executive" ? "#059669" : effectiveTier === "business" ? "#D97706" : "#64748B" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0F172A" }}>
              {effectiveTier === "executive"
                ? "Executive & Financial Suite (Full Master Suite: Calculators 1 – 9)"
                : effectiveTier === "business"
                ? "Business Officer Suite (Baseline 1–4 + Business Tenor Modification & Exposure Calculators)"
                : "Standard Financial Reference (Baseline Calculators 1 – 4)"}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            {effectiveTier === "executive"
              ? "Authorized for Financial I, II, III, President, and Administrator with full mathematical formulas and ledger metrics."
              : effectiveTier === "business"
              ? "Authorized for Business Officers with commodity financing, profit markups, and tenor restructuring delta calculations."
              : "Standard member reference displaying baseline statutory stamp duty, multi-account splits, and trade margins."}
          </Typography>
        </Box>

        {isExecutiveOrFinancial && (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              Preview Tier:
            </Typography>
            <Chip
              label="All (Executive)"
              size="small"
              clickable
              color={effectiveTier === "executive" ? "success" : "default"}
              onClick={() => setPreviewTier("executive")}
              sx={{ fontWeight: 700, fontSize: "0.72rem" }}
            />
            <Chip
              label="Business View"
              size="small"
              clickable
              color={effectiveTier === "business" ? "warning" : "default"}
              onClick={() => setPreviewTier("business")}
              sx={{ fontWeight: 700, fontSize: "0.72rem" }}
            />
            <Chip
              label="Member View (1-4)"
              size="small"
              clickable
              color={effectiveTier === "member" ? "primary" : "default"}
              onClick={() => setPreviewTier("member")}
              sx={{ fontWeight: 700, fontSize: "0.72rem" }}
            />
          </Box>
        )}
      </Paper>

      {/* 1. EMTL Stamp Duty Card */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <VerifiedUserRoundedIcon sx={{ color: "#059669" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              1. EMTL Stamp Duty Simulator (₦50 Statutory Rule)
            </Typography>
          </Box>
          <Chip label="Section 2.1 Formula" size="small" variant="outlined" />
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Deposit Amount"
                type="text"
                value={emtlDeposit}
                onChange={(e) => setEmtlDeposit(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
                helperText="Qualifies for ₦50 duty when amount is ₦10,000 or greater"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={emtlWaived}
                    onChange={(e) => setEmtlWaived(e.target.checked)}
                    color="primary"
                  />
                }
                label="Waive Stamp Duty (Officer Exception)"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: emtlDuty > 0 ? "#FFFBEB" : "#ECFDF5",
                  border: `1px solid ${emtlDuty > 0 ? "#FDE68A" : "#A7F3D0"}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  SIMULATION RESULTS
                </Typography>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2">Qualifying Status:</Typography>
                  <Chip
                    label={isQualifying ? "Qualifying (≥ ₦10,000)" : "Below Threshold (< ₦10,000)"}
                    size="small"
                    color={isQualifying ? "primary" : "default"}
                    sx={{ height: 22, fontSize: "0.72rem" }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2">Stamp Duty Deducted:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: emtlDuty > 0 ? "error.main" : "text.secondary" }}>
                    ₦{fNumber(emtlDuty)} {emtlWaived ? "(Waived)" : ""}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Net Credited to Member:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "success.main" }}>
                    ₦{fNumber(emtlNet)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 2. Single Deposit Split Tool Simulator */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalculateRoundedIcon sx={{ color: "#0B3B60" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
              2. Single Deposit Split Tool Simulator (5 Accounts + 70/30 Formula)
            </Typography>
          </Box>
          <Chip label="Section 2.2 Formula" size="small" variant="outlined" />
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Total Deposit (D)"
                type="text"
                value={splitDeposit}
                onChange={(e) => setSplitDeposit(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Loan Repay"
                type="text"
                value={splitLoan}
                onChange={(e) => setSplitLoan(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Ileya"
                type="text"
                value={splitIleya}
                onChange={(e) => setSplitIleya(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Takaful"
                type="text"
                value={splitTakaful}
                onChange={(e) => setSplitTakaful(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={splitDutyWaived}
                    onChange={(e) => setSplitDutyWaived(e.target.checked)}
                    size="small"
                  />
                }
                label="Waive Duty"
              />
            </Grid>

            {splitDeficit > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontWeight: 600 }}>
                  Deposit Deficit Alert! Deductions (₦{fNumber(splitFixed)}) exceed deposit by ₦{fNumber(splitDeficit)}.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: "#F1F5F9",
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                  gap: { xs: 1.5, sm: 2 }
                }}
              >
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.7rem" }}>
                    STAMP DUTY (S)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                    ₦{fNumber(splitDuty)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.7rem" }}>
                    RESIDUAL BALANCE
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                    ₦{fNumber(splitResidual)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.7rem" }}>
                    SAVINGS (70%)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#059669", fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                    ₦{fNumber(splitSavings)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.7rem" }}>
                    SHARES (30%)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60", fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                    ₦{fNumber(splitShares)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 3. Welfare Dues 3-Way Split */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentsRoundedIcon sx={{ color: "#D97706" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
              3. Welfare & Emergency Dues Split (3-Way Equal Allocation)
            </Typography>
          </Box>
          <Chip label="Section 2.3 Formula" size="small" variant="outlined" />
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Alert
            severity="info"
            sx={{
              mb: 2.5,
              borderRadius: 2,
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              color: "#1E40AF",
              "& .MuiAlert-icon": { color: "#2563EB" }
            }}
          >
            <strong>Operational Caveat:</strong> While this tool demonstrates a 3-way equal allocation (33.34% Welfare, 33.33% Minutes, 33.33% Development), in most operational cases the Financial Secretary or Assistant can manually enter the exact amount in each account box (Welfare, Minutes, Development) to reflect the specific dues schedule or custom breakdown of the member.
          </Alert>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Total Dues Deposit"
                type="text"
                value={duesDeposit}
                onChange={(e) => setDuesDeposit(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={duesWaived}
                    onChange={(e) => setDuesWaived(e.target.checked)}
                    size="small"
                  />
                }
                label="Waive 1x Stamp Duty"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1.5
                }}
              >
                <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    WELFARE (33.34%)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#16A34A" }}>
                    ₦{fNumber(duesWelfare)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    MINUTES (33.33%)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0284C7" }}>
                    ₦{fNumber(duesMinutes)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "#FAF5FF", border: "1px solid #E9D5FF", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    DEVELOPMENT (33.33%)
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#9333EA" }}>
                    ₦{fNumber(duesDev)}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 4. Business Commodity Profit Calculator */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessCenterRoundedIcon sx={{ color: "#0B3B60" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
              4. Business Commodity Financing & Profit Markup Calculator
            </Typography>
          </Box>
          <Chip label="Section 2.4 Formula" size="small" variant="outlined" />
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Base Item Cost (C)"
                type="text"
                value={bizCost}
                onChange={(e) => setBizCost(formatInputWithCommas(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.5 }}>
                Select Repayment Tenor
              </Typography>
              <Box display="flex" gap={1}>
                {[3, 6, 9].map((m) => (
                  <Button
                    key={m}
                    size="small"
                    variant={bizTenor === m ? "contained" : "outlined"}
                    onClick={() => {
                      setBizTenor(m);
                      setBizRate(m === 3 ? 5 : m === 6 ? 10 : 12);
                    }}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 700, py: 0.75 }}
                  >
                    {m}M ({m === 3 ? "5%" : m === 6 ? "10%" : "12%"})
                  </Button>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Custom Margin %"
                type="number"
                value={bizRate}
                onChange={(e) => setBizRate(Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: "#F8FAFC",
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: { xs: 1.5, sm: 2 }
                }}
              >
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    PROFIT MARGIN (P)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#D97706" }}>
                    ₦{fNumber(bizProfit)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    TOTAL MEMBER DEBT (A)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                    ₦{fNumber(bizTotal)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    MONTHLY INSTALLMENT
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#059669" }}>
                    ₦{fNumber(bizInstallment)} / mo
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 5. Business Tenor Restructuring & Margin Modification Delta (Visible to Business Officer + Executive/Financial) */}
      {canSeeBusinessExtras && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "#FFFBEB",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon sx={{ color: "#D97706" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                5. Business Tenor Restructuring & Margin Modification Delta Calculator
              </Typography>
            </Box>
            <Chip label="Section 2.4.5 Formula" size="small" color="warning" variant="outlined" />
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Calculates the financial delta and monthly installment adjustment when a member requests extending or restructuring their asset financing tenor (e.g. extending from 6M to 9M). Preserved in <code>tenorHistory</code> for audit integrity.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vendor Base Cost (C)"
                  type="text"
                  value={restructCost}
                  onChange={(e) => setRestructCost(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                  helperText="Invoice price paid to supplier"
                />
              </Grid>

              {/* Original Terms */}
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 1.5, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B", textTransform: "uppercase" }}>
                    Original Agreement (T₁)
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <TextField
                      size="small"
                      label="Tenor (T₁)"
                      type="number"
                      value={origTenor}
                      onChange={(e) => setOrigTenor(Number(e.target.value) || 1)}
                      InputProps={{ endAdornment: <InputAdornment position="end">M</InputAdornment> }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Margin %"
                      type="number"
                      value={origMargin}
                      onChange={(e) => setOrigMargin(Number(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                    Previous Debt: <strong>₦{fNumber(origTotal)}</strong> (₦{fNumber(origInstallment)}/mo)
                  </Typography>
                </Paper>
              </Grid>

              {/* Restructured Terms */}
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 1.5, bgcolor: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#B45309", textTransform: "uppercase" }}>
                    Restructured Agreement (T₂)
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <TextField
                      size="small"
                      label="New Tenor (T₂)"
                      type="number"
                      value={newTenor}
                      onChange={(e) => setNewTenor(Number(e.target.value) || 1)}
                      InputProps={{ endAdornment: <InputAdornment position="end">M</InputAdornment> }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="New Margin %"
                      type="number"
                      value={newMargin}
                      onChange={(e) => setNewMargin(Number(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#B45309" }}>
                    New Debt: <strong>₦{fNumber(newTotal)}</strong> (₦{fNumber(newInstallment)}/mo)
                  </Typography>
                </Paper>
              </Grid>

              {/* Delta Comparison Summary */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#F1F5F9",
                    borderRadius: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                    gap: 2
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      PREVIOUS TOTAL DEBT (A₁)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#475569" }}>
                      ₦{fNumber(origTotal)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      RESTRUCTURED DEBT (A₂)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                      ₦{fNumber(newTotal)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      DEBT DELTA (ΔA)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: deltaDebt >= 0 ? "#B45309" : "#059669" }}>
                      {deltaDebt >= 0 ? "+" : ""}₦{fNumber(deltaDebt)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      MONTHLY CASHFLOW DELTA (ΔI)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: deltaInstallment <= 0 ? "#059669" : "#B45309" }}>
                      {deltaInstallment <= 0 ? "" : "+"}₦{fNumber(deltaInstallment)} / mo
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 6. Business Commodity Repayment & Exposure Balance Simulator (Visible to Business Officer + Executive/Financial) */}
      {canSeeBusinessExtras && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "#F8FAFC",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon sx={{ color: "#0B3B60" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                6. Business Commodity Repayment & Portfolio Exposure Simulator
              </Typography>
            </Box>
            <Chip label="Section 2.6 Liability Ledger" size="small" variant="outlined" />
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Total Financed Amount (A)"
                  type="text"
                  value={expTotalFinanced}
                  onChange={(e) => setExpTotalFinanced(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cumulative Repaid Capital"
                  type="text"
                  value={expRepaid}
                  onChange={(e) => setExpRepaid(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Agreed Monthly Installment"
                  type="text"
                  value={expMonthlyDue}
                  onChange={(e) => setExpMonthlyDue(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                />
              </Grid>

              {/* Progress Bar */}
              <Grid item xs={12}>
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      Repayment Completion Progress
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#059669" }}>
                      {expProgress}% Repaid
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={expProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: "#E2E8F0",
                      "& .MuiLinearProgress-bar": { backgroundColor: "#059669", borderRadius: 5 }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#F8FAFC",
                    borderRadius: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                    gap: 2
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      OUTSTANDING EXPOSURE
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: expBalance > 0 ? "error.main" : "success.main" }}>
                      ₦{fNumber(expBalance)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      RECOVERED CAPITAL
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#059669" }}>
                      ₦{fNumber(cleanExpRepaid)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      EST. MONTHS TO SETTLEMENT
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                      {expMonthsLeft} Month{expMonthsLeft === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 7. Cooperative Loan Facility, COT & Amortization Simulator (Visible to Executive/Financial) */}
      {canSeeFinancialExtras && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "#F0FDF4",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CreditScoreIcon sx={{ color: "#059669" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                7. Cooperative Loan Facility, COT & Amortization Simulator
              </Typography>
            </Box>
            <Chip label="Section 2.5 Formula" size="small" color="success" variant="outlined" />
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Underwrites cooperative loan disbursements, computes Cost of Transaction (COT: 0.1%), enforces standard ₦1,000 application form fee, and calculates monthly amortization schedules.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Principal Loan Amount (P)"
                  type="text"
                  value={loanPrincipal}
                  onChange={(e) => setLoanPrincipal(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.5 }}>
                  Cost of Transaction Rate (r_cot)
                </Typography>
                <Box display="flex" gap={1}>
                  {[0.1, 0.5, 1.0].map((rate) => (
                    <Button
                      key={rate}
                      size="small"
                      variant={loanCotRate === rate ? "contained" : "outlined"}
                      onClick={() => setLoanCotRate(rate)}
                      sx={{ flex: 1, textTransform: "none", fontWeight: 700, py: 0.75 }}
                    >
                      {rate}%
                    </Button>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.5 }}>
                  Loan Tenor (Months)
                </Typography>
                <Box display="flex" gap={1}>
                  {[3, 6, 12].map((tenor) => (
                    <Button
                      key={tenor}
                      size="small"
                      variant={loanTenor === tenor ? "contained" : "outlined"}
                      onClick={() => setLoanTenor(tenor)}
                      sx={{ flex: 1, textTransform: "none", fontWeight: 700, py: 0.75 }}
                    >
                      {tenor} Mos
                    </Button>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#F8FAFC",
                    borderRadius: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(5, 1fr)" },
                    gap: 1.5
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      COT FEE AMOUNT
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#D97706" }}>
                      ₦{fNumber(loanCotAmount)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      TOTAL REPAYMENT DEBT
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                      ₦{fNumber(loanTotalDebt)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      MONTHLY AMORTIZATION
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#059669" }}>
                      ₦{fNumber(loanMonthlyAmort)} / mo
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      FORM FEE (FLAT)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#475569" }}>
                      ₦{fNumber(loanFormFee)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      GUARANTORS REQUIRED
                    </Typography>
                    <Chip label="2 Active Members" size="small" color="primary" sx={{ fontWeight: 700, height: 24, mt: 0.5 }} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 8. EMTL Stamp Duty Portfolio Aggregations & Member Capital Preserved (Visible to Executive/Financial) */}
      {canSeeFinancialExtras && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "#F8FAFC",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <HandshakeIcon sx={{ color: "#0284C7" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                8. EMTL Stamp Duty Portfolio Aggregations & Member Capital Preserved
              </Typography>
            </Box>
            <Chip label="Section 2.1 Aggregations" size="small" variant="outlined" />
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Demonstrates portfolio-wide electronic deposit metrics, statutory ₦50 duty deductions, waiver counts, and the exact volume of capital preserved for members.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Qualifying Deposits (≥₦10,000)"
                  type="text"
                  value={aggQualifyingCount}
                  onChange={(e) => setAggQualifyingCount(formatInputWithCommas(e.target.value))}
                  InputProps={{ inputMode: "numeric" }}
                  helperText="Total electronic deposits meeting threshold"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Average Deposit Size"
                  type="text"
                  value={aggAvgAmount}
                  onChange={(e) => setAggAvgAmount(formatInputWithCommas(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Authorized Waived Deposits (Nw)"
                  type="text"
                  value={aggWaivedCount}
                  onChange={(e) => setAggWaivedCount(formatInputWithCommas(e.target.value))}
                  InputProps={{ inputMode: "numeric" }}
                  helperText="Duty waived by Financial Officer"
                />
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#F1F5F9",
                    borderRadius: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                    gap: 2
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      TOTAL QUALIFYING VOLUME
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                      ₦{fNumber(aggVolume)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      STATUTORY DUTY DEDUCTED
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "error.main" }}>
                      ₦{fNumber(aggTotalDeducted)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      ({aggDeductedCount} deposits)
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      CAPITAL PRESERVED (WAIVERS)
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "success.main" }}>
                      ₦{fNumber(aggCapitalPreserved)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      ({cleanAggWaived} waived)
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      NET CAPITAL CREDITED
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0284C7" }}>
                      ₦{fNumber(aggNetMemberCredit)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 9. 10-Product Running Balance Continuity & Ledger Integrity Simulator (Visible to Executive/Financial) */}
      {canSeeFinancialExtras && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "#F8FAFC",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <SyncAltIcon sx={{ color: "#9333EA" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                9. 10-Product Running Balance Continuity & Ledger Integrity Simulator
              </Typography>
            </Box>
            <Chip label="Section 2.6 Balance Equations" size="small" variant="outlined" />
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Simulates exact balance continuity across Asset/Contribution accounts (Credit increases balance, Debit decreases balance) versus Liability accounts (Disbursement increases debt, Repayment decreases debt).
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.5 }}>
                  Select Account Category
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant={balAccountType === "asset" ? "contained" : "outlined"}
                    onClick={() => setBalAccountType("asset")}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    Asset / Contribution (Savings, Shares, SpeAcc, Dev, Min, Wel, Ileya, Takaful)
                  </Button>
                  <Button
                    size="small"
                    variant={balAccountType === "liability" ? "contained" : "outlined"}
                    onClick={() => setBalAccountType("liability")}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    Liability / Debt (Loan, Business Trade)
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Grid container spacing={1.5}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Opening Balance"
                      type="text"
                      value={balStarting}
                      onChange={(e) => setBalStarting(formatInputWithCommas(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        inputMode: "numeric"
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={balAccountType === "asset" ? "Deposit (+)" : "Disbursement (+)"}
                      type="text"
                      value={balCredit}
                      onChange={(e) => setBalCredit(formatInputWithCommas(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        inputMode: "numeric"
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={balAccountType === "asset" ? "Withdrawal (-)" : "Repayment (-)"}
                      type="text"
                      value={balDebit}
                      onChange={(e) => setBalDebit(formatInputWithCommas(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        inputMode: "numeric"
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#FAF5FF",
                    border: "1px solid #E9D5FF",
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      MATHEMATICAL FORMULA
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#6B21A8", fontFamily: "monospace" }}>
                      {balAccountType === "asset"
                        ? `Balance_new = ${fNumber(cleanBalStarting)} + ${fNumber(cleanBalCredit)} - ${fNumber(cleanBalDebit)}`
                        : `Remaining_Debt = max(0, ${fNumber(cleanBalStarting)} + ${fNumber(cleanBalCredit)} - ${fNumber(cleanBalDebit)})`}
                    </Typography>
                  </Box>

                  <Box textAlign={{ xs: "left", sm: "right" }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      CLOSING VERIFIED BALANCE
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#581C87" }}>
                      ₦{fNumber(balEnding)}
                    </Typography>
                    <Chip
                      label={balNetDelta >= 0 ? `Net Flow: +₦${fNumber(balNetDelta)}` : `Net Flow: -₦${fNumber(Math.abs(balNetDelta))}`}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, mt: 0.5 }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main Interactive System Guide Page
// ----------------------------------------------------------------------

export default function SystemGuide() {
  const theme = useTheme();

  // Determine if the current user has admin privileges
  const userIsAdmin = useMemo(() => {
    try {
      const viewAs = sessionStorage.getItem("mtc_view_as_role");
      if (viewAs) {
        return viewAs.toLowerCase().includes("admin");
      }
      const rawUser = localStorage.getItem("user") || localStorage.getItem("profile");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u.isAdmin) return true;
        const roles = Array.isArray(u.roles) ? u.roles.join(" ") : String(u.role || u.roles || "");
        return roles.toLowerCase().includes("admin");
      }
    } catch {
      // fallback
    }
    return false;
  }, []);

  // Filter available role guides: Admin guide is strictly hidden unless user is Admin
  const availableRoles = useMemo(() => {
    return Object.entries(ROLE_DATA).filter(([key]) => {
      if (key === "admin" && !userIsAdmin) {
        return false;
      }
      return true;
    });
  }, [userIsAdmin]);

  // Determine current active user role
  const initialRole = useMemo(() => {
    try {
      const viewAs = sessionStorage.getItem("mtc_view_as_role");
      if (viewAs) {
        const v = viewAs.toLowerCase().replace(/role_/g, "");
        if (v.includes("admin") && userIsAdmin) return "admin";
        if (v.includes("president")) return "president";
        if (v.includes("financial_1") || (v.includes("financial") && !v.includes("financial_2") && !v.includes("financial_3"))) return "financial_1";
        if (v.includes("financial_2")) return "financial_2";
        if (v.includes("financial_3")) return "financial_3";
        if (v.includes("business")) return "business";
        if (v.includes("registrar") || v.includes("secretary")) return "registrar";
      }
      const rawUser = localStorage.getItem("user") || localStorage.getItem("profile");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u.isAdmin && userIsAdmin) return "admin";
        const roles = Array.isArray(u.roles) ? u.roles.join(" ") : String(u.role || u.roles || "");
        const lower = roles.toLowerCase().replace(/role_/g, "");
        if (lower.includes("admin") && userIsAdmin) return "admin";
        if (u.isPresident || lower.includes("president")) return "president";
        if (lower.includes("financial_1") || (lower.includes("financial") && !lower.includes("financial_2") && !lower.includes("financial_3"))) return "financial_1";
        if (lower.includes("financial_2")) return "financial_2";
        if (lower.includes("financial_3")) return "financial_3";
        if (lower.includes("business")) return "business";
        if (lower.includes("registrar") || lower.includes("secretary")) return "registrar";
        if (lower.includes("user") || lower.includes("member")) return "member";
      }
    } catch {
      // fallback
    }
    return "member";
  }, [userIsAdmin]);

  const [selectedRole, setSelectedRole] = useState(() => {
    if (initialRole === "admin" && !userIsAdmin) {
      return "financial_1";
    }
    return initialRole;
  });
  const [activeTab, setActiveTab] = useState(0); // 0: Role Guide, 1: Interactive Calculators
  const [searchQuery, setSearchQuery] = useState("");

  const effectiveRole = (selectedRole === "admin" && !userIsAdmin) ? "financial_1" : selectedRole;
  const currentRoleInfo = ROLE_DATA[effectiveRole] || ROLE_DATA.financial_1;
  const RoleIcon = currentRoleInfo.icon;

  // Filter workflows or faqs by search query
  const filteredWorkflows = useMemo(() => {
    if (!searchQuery) return currentRoleInfo.workflows || [];
    const q = searchQuery.toLowerCase();
    return (currentRoleInfo.workflows || []).filter(
      (w) =>
        w.title.toLowerCase().includes(q) ||
        w.steps.some((s) => s.toLowerCase().includes(q))
    );
  }, [currentRoleInfo, searchQuery]);

  const filteredFaqs = useMemo(() => {
    if (!searchQuery) return currentRoleInfo.faqs || [];
    const q = searchQuery.toLowerCase();
    return (currentRoleInfo.faqs || []).filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    );
  }, [currentRoleInfo, searchQuery]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      {/* Top Header */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <MenuBookRoundedIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "primary.main" }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.1rem" }
            }}
          >
            Role Guides & Calculation Reference
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          Interactive role-based operating manual, security permissions matrix, and mathematical calculation simulators.
        </Typography>
      </Box>

      {/* Navigation Mode Tabs (Scrollable on Mobile) */}
      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: { xs: 2, sm: 3 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 700,
            fontSize: { xs: "0.82rem", sm: "0.95rem" },
            minHeight: 46,
            px: { xs: 1.5, sm: 2.5 }
          }
        }}
      >
        <Tab icon={<SecurityRoundedIcon />} iconPosition="start" label="Role Operations Manual" />
        <Tab icon={<CalculateRoundedIcon />} iconPosition="start" label="Interactive Calculators Playground" />
      </Tabs>

      {activeTab === 0 ? (
        <>
          {/* Mobile Horizontal Scrollable Role Pills (Visible ONLY on Mobile < 900px) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              gap: 1,
              pb: 1.5,
              mb: 2,
              px: 0.5,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.18)",
                borderRadius: 2
              }
            }}
          >
            {availableRoles.map(([key, role]) => {
              const IconComponent = role.icon;
              const isSelected = selectedRole === key;
              return (
                <Chip
                  key={key}
                  icon={
                    <IconComponent
                      sx={{
                        fontSize: 18,
                        color: isSelected ? "#FFFFFF !important" : `${role.badgeColor} !important`
                      }}
                    />
                  }
                  label={role.shortName}
                  clickable
                  onClick={() => setSelectedRole(key)}
                  sx={{
                    height: 38,
                    px: 1,
                    fontWeight: isSelected ? 800 : 600,
                    fontSize: "0.8rem",
                    backgroundColor: isSelected ? role.badgeColor : "background.paper",
                    color: isSelected ? "#FFFFFF" : "text.primary",
                    border: `1px solid ${isSelected ? role.badgeColor : theme.palette.divider}`,
                    boxShadow: isSelected ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
                    flexShrink: 0
                  }}
                />
              );
            })}
          </Box>

          <Grid container spacing={3}>
            {/* Desktop Left Column: Role Selector Pills (Hidden on Mobile) */}
            <Grid item xs={12} md={3.5} sx={{ display: { xs: "none", md: "block" } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: "background.paper"
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "block",
                    mb: 1.5
                  }}
                >
                  Select Account Role to Inspect
                </Typography>

                <Stack spacing={1}>
                  {availableRoles.map(([key, role]) => {
                    const IconComponent = role.icon;
                    const isSelected = selectedRole === key;
                    return (
                      <Button
                        key={key}
                        onClick={() => setSelectedRole(key)}
                        variant={isSelected ? "contained" : "text"}
                        startIcon={
                          <IconComponent sx={{ color: isSelected ? "#FFFFFF" : role.badgeColor }} />
                        }
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          py: 1.25,
                          px: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: isSelected ? 800 : 600,
                          backgroundColor: isSelected ? role.badgeColor : "transparent",
                          color: isSelected ? "#FFFFFF" : "text.primary",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? role.badgeColor
                              : alpha(role.badgeColor, 0.08)
                          }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: "inherit", lineHeight: 1.2 }}>
                            {role.shortName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isSelected ? "rgba(255,255,255,0.8)" : "text.secondary",
                              fontSize: "0.7rem",
                              display: "block"
                            }}
                          >
                            {role.scope}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column: Selected Role Details */}
            <Grid item xs={12} md={8.5}>
              <Stack spacing={2.5}>
                {/* Role Header Banner */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2.5,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "background.paper"
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      mb: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: alpha(currentRoleInfo.badgeColor, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: currentRoleInfo.badgeColor,
                        flexShrink: 0
                      }}
                    >
                      <RoleIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                      >
                        {currentRoleInfo.title}
                      </Typography>
                      <Chip
                        label={`Scope: ${currentRoleInfo.scope}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: alpha(currentRoleInfo.badgeColor, 0.12),
                          color: currentRoleInfo.badgeColor,
                          mt: 0.5,
                          maxWidth: "100%"
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.6,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" }
                    }}
                  >
                    {currentRoleInfo.summary}
                  </Typography>
                </Paper>

              {/* Permissions & Security Boundaries */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2.5,
                      border: "1px solid #BBF7D0",
                      backgroundColor: "#F0FDF4"
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <CheckCircleRoundedIcon sx={{ color: "#16A34A" }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534" }}>
                          Permitted Capabilities & Actions
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {currentRoleInfo.allowed.map((item, idx) => (
                          <Box key={idx} display="flex" alignItems="flex-start" gap={1}>
                            <Typography variant="caption" sx={{ color: "#16A34A", fontWeight: 800, mt: 0.25 }}>
                              ✓
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#14532D" }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2.5,
                      border: "1px solid #FECDD3",
                      backgroundColor: "#FFF1F2"
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <CancelRoundedIcon sx={{ color: "#DC2626" }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#991B1B" }}>
                          System Restrictions & Boundaries
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {currentRoleInfo.restricted.map((item, idx) => (
                          <Box key={idx} display="flex" alignItems="flex-start" gap={1}>
                            <Typography variant="caption" sx={{ color: "#DC2626", fontWeight: 800, mt: 0.25 }}>
                              ✕
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#7F1D1D" }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Workflows Accordion */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: "background.paper"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
                    Step-by-Step Operational Workflows
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search workflows & FAQs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: { xs: "100%", sm: 260 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                {filteredWorkflows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No matching workflows found for this role.
                  </Typography>
                ) : (
                  filteredWorkflows.map((wf, idx) => (
                    <Accordion
                      key={idx}
                      defaultExpanded={idx === 0}
                      sx={{
                        mb: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                        boxShadow: "none"
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: currentRoleInfo.badgeColor }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", sm: "0.95rem" } }}>
                            {wf.title}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 2, px: { xs: 1.5, sm: 2 } }}>
                        <Stack spacing={1.25}>
                          {wf.steps.map((step, sIdx) => (
                            <Box key={sIdx} display="flex" alignItems="flex-start" gap={1.5}>
                              <Box
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  backgroundColor: alpha(currentRoleInfo.badgeColor, 0.12),
                                  color: currentRoleInfo.badgeColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.75rem",
                                  fontWeight: 800,
                                  flexShrink: 0,
                                  mt: 0.2
                                }}
                              >
                                {sIdx + 1}
                              </Box>
                              <Typography variant="body2" sx={{ fontSize: { xs: "0.82rem", sm: "0.875rem" }, color: "text.primary" }}>
                                {step}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}

                {/* Frequently Asked Questions */}
                {filteredFaqs.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                      Frequently Asked Questions ({currentRoleInfo.shortName})
                    </Typography>
                    {filteredFaqs.map((faq, fIdx) => (
                      <Accordion
                        key={fIdx}
                        sx={{
                          mb: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "8px !important",
                          "&:before": { display: "none" },
                          boxShadow: "none"
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", fontSize: { xs: "0.82rem", sm: "0.9rem" } }}>
                            {faq.q}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 2, px: { xs: 1.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                            {faq.a}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
        </>
      ) : (
        /* Interactive Calculators Playground */
        <InteractiveCalculators
          userRole={initialRole}
          userIsAdmin={userIsAdmin}
          selectedGuideRole={effectiveRole}
        />
      )}
    </Container>
  );
}
